import {Card, FormControl, FormField, Label, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Spinner, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {
  GetArticleResponse,
  usePutArticleStockManagementCalculationMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {ArticleDetailForm} from '../types/ArticleDetailFormSchema';

interface GeneralSettingsCardProps {
  stockManagement: GetArticleResponse['stockManagement'];
  control: FormControl<ArticleDetailForm>;
  formApi: UseFormReturn<ArticleDetailForm>;
  handlingUnitLabel: string;
  onChange: VoidFunction;
}

export function GeneralSettingsCard(props: GeneralSettingsCardProps) {
  const [, calculateStockStatus] = usePutArticleStockManagementCalculationMutation({
    fixedCacheKey: 'stock-management-calculation',
  });

  const stockManagementType = useWatch({
    control: props.control,
    name: 'stockManagement.stockManagementType',
  });

  const stockManagementRadioGroupName = 'stockManagementType';

  const handleManualOrderOnly = () => {
    props.formApi.setValue('stockManagement.stockManagementType', 'MANUAL');
  };

  const handleMinMaxOrdering = () => {
    props.formApi.setValue('stockManagement.stockManagementType', 'MIN_MAX');
  };

  return (
    <Card hasSeparator title={i18n.t('entity.warehouse.labels.generalSettings')}>
      <VStack spacing={4}>
        <Card
          variant="inlineGrey"
          title={i18n.t('entity.warehouse.labels.manualOrderOnly')}
          control={{
            type: 'radio',
            name: stockManagementRadioGroupName,
            value: 'MANUAL' === stockManagementType,
            onChange: handleManualOrderOnly,
          }}
        />
        <Box position="relative" width="100%" height="100%">
          <Show when={calculateStockStatus.isLoading}>
            <Spinner variant="overlay" />
          </Show>
          <Card
            variant="inlineGrey"
            title={i18n.t('entity.warehouse.labels.minMaxOrdering')}
            control={{
              type: 'radio',
              name: stockManagementRadioGroupName,
              value: 'MIN_MAX' === stockManagementType,
              onChange: handleMinMaxOrdering,
            }}
            isExpanded={'MIN_MAX' === stockManagementType}
          >
            <HStack spacing={4}>
              <Box width={65}>
                <FormField
                  control={props.control}
                  name="stockManagement.stockManagementMinQuantity"
                  type="number"
                  label={i18n.t('entity.warehouse.labels.minimumStock')}
                  suffix={props.handlingUnitLabel}
                  onChange={props.onChange}
                />
              </Box>
              <Box width={65}>
                <FormField
                  control={props.control}
                  name="stockManagement.stockManagementMaxQuantity"
                  type="number"
                  label={i18n.t('entity.warehouse.labels.maximumStock')}
                  suffix={props.handlingUnitLabel}
                  onChange={props.onChange}
                />
              </Box>

              <VStack>
                <Label tooltip={i18n.t('entity.warehouse.labels.toOrderTooltip')}>
                  {i18n.t('entity.warehouse.labels.toOrder')}
                </Label>
                <Heading size={2} color="success">
                  {`${props.stockManagement.toOrderQuantity ?? 0} ${props.handlingUnitLabel}`}
                </Heading>
              </VStack>
            </HStack>
          </Card>
        </Box>
        <HStack align="center" spacing={1}>
          <FormField
            control={props.control}
            name="stockManagement.isOneTimeSale"
            type="checkbox"
            label={i18n.t('entity.warehouse.labels.oneTimeSaleByDefault')}
          />
          <Tooltip description={i18n.t('entity.warehouse.labels.oneTimeSaleByDefaultTooltip')}>
            <Icon value="action/help" color="text.secondary" size={4} />
          </Tooltip>
        </HStack>
      </VStack>
    </Card>
  );
}
